import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { isNoneColor } from '@src/utility/color';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import ColorPicker, { type ColorTabs } from './ColorPicker';

type Props = {
  showBorderColor?: boolean;
  action: (propertyName: string, value: string | null) => void;
  previewAction?: (propertyName: string, value: string | null) => void;
  propertyName: string;
  defaultValue: string | undefined | null;
  getValue?: (value: string) => void;
  pickerTab?: ColorTabs;
};

const ArrowDownIcon = (
  <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
    <path d="M2 4.33008L6 8.33008L10 4.33008" stroke="var(--accent)" />
  </svg>
);

function toDisplayHex(
  value: string | null | undefined,
  rgbaToHex: (s: string, skip?: boolean) => string,
): string {
  if (!value) return '';
  const hex = rgbaToHex(value, true);
  if (hex) return hex.slice(0, 7).toUpperCase();
  if (value.startsWith('#')) return value.slice(0, 7).toUpperCase();
  return '';
}

// Erasing applies a fully-transparent color rather than null: a null color
// defaults to black in the renderer, which contradicts the "None" label. A
// transparent fill renders as nothing, matching what the label promises.
export const TRANSPARENT_COLOR = 'rgba(0, 0, 0, 0)';

const ColorDropdownSelect = (props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const [selectedItem, setSelectedItem] = useState<string | undefined | null>(
    props.defaultValue,
  );
  const [previewColor, setPreviewColor] = useState<string | null>(null);
  const [colorPicker, togglePicker] = useState<DOMRect | null>(null);
  const pickerClose = videoCreator.textBrandingService.closeActiveState;
  useEffect(() => {
    if (pickerClose) {
      togglePicker(null);
      videoCreator.textBrandingService.closeActiveState = false;
    }
  }, [pickerClose]);

  useEffect(() => {
    setSelectedItem(props.defaultValue);
  }, [props.defaultValue]);

  // `previewColor` is a transient hover-only override of the trigger swatch/hex.
  // It must never outlive the open picker, otherwise it shadows the real
  // committed value (e.g. after undo, a Custom-tab pick, or an external change
  // the trigger would keep showing a stale hovered color). Clear it whenever
  // the picker closes by any means (select, dismiss, external close).
  useEffect(() => {
    if (!colorPicker) setPreviewColor(null);
  }, [colorPicker]);

  // Dismissal (click-outside, scroll, resize, Escape) is owned by ColorPicker
  // itself once it portals to document.body — no wrapper-side ref needed.

  const displayColor = previewColor ?? selectedItem;
  const displayHex = isNoneColor(displayColor)
    ? ''
    : toDisplayHex(
        displayColor,
        videoCreator.textBrandingService.rgbaToHex.bind(
          videoCreator.textBrandingService,
        ),
      );

  return (
    <Main>
      <Selected
        onClick={e => {
          if (colorPicker) return togglePicker(null);
          return togglePicker(e.currentTarget.getBoundingClientRect());
        }}
      >
        <SwatchRow>
          <SelectedContent
            selectedItem={
              !props.showBorderColor && isNoneColor(displayColor)
                ? null
                : displayColor
            }
            showBorderColor={props.showBorderColor}
          />
          {<HexLabel>{displayHex || 'None'}</HexLabel>}
        </SwatchRow>
        <ToggleIcon>{ArrowDownIcon}</ToggleIcon>
      </Selected>

      {colorPicker && (
        <ColorPicker
          // Open from the locally-committed value, not props.defaultValue.
          // props.defaultValue is derived from the composition source, which
          // lags behind a fresh selection (it round-trips through the debounced
          // renderer sync). selectedItem is set synchronously on every commit
          // and also tracks defaultValue, so it is always at least as fresh —
          // this keeps the reopened picker's checkmark + bottom bar correct.
          defaultValue={selectedItem}
          getValue={color => {
            // Committing supersedes any hover preview — drop the override so the
            // trigger reflects the committed value, even while the picker stays
            // open (e.g. choosing on the Custom tab after hovering a preset).
            setPreviewColor(null);
            // Erase sends null; apply transparent instead so the render matches
            // the "None" label rather than the renderer defaulting to black.
            const next = color ?? TRANSPARENT_COLOR;
            setSelectedItem(next);
            props.action(props.propertyName, next);
          }}
          onPreview={color => {
            setPreviewColor(color);
            props.previewAction?.(props.propertyName, color);
          }}
          solidDefaultAlpha
          pos={colorPicker}
          onClose={() => {
            togglePicker(null);
          }}
          tab={props.pickerTab}
        />
      )}
    </Main>
  );
};

export default ColorDropdownSelect;

const Main = styled.div`
  position: relative;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  width: 100%;
`;
const Selected = styled.div`
  border: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  padding: 8px 16px;
  border-radius: 8px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);

  /*
   * Bordered-field-primitive hover standard — see
   * docs/design-system.md "Form Fields (Bordered Primitives)".
   * No focus-visible state here because this is a <div>, not natively
   * focusable. A future a11y pass should add tabindex + role + the
   * focus-visible accent ring; that's beyond the scope of the hover
   * standard adoption.
   */
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: var(--text-muted);
  }
`;

const SelectedContent = styled.span<{
  selectedItem?: string | null;
  showBorderColor?: boolean;
}>`
  font-size: var(--text-sm);

  ${props =>
    !props.showBorderColor &&
    props.selectedItem &&
    css`
      width: 15px;
      height: 15px;
      background-color: ${props.selectedItem};
      border: ${(props.selectedItem === 'black' ||
        props.selectedItem === 'rgba(0, 0, 0, 1)') &&
      '1px solid var(--border)'};
      border-radius: 50%;
    `}

  ${props =>
    props.showBorderColor &&
    props.selectedItem &&
    css`
      width: 15px;
      height: 15px;
      border: 1px solid ${props.selectedItem};
      background: ${props.selectedItem === 'black' ||
      props.selectedItem === 'rgba(0, 0, 0, 1)'
        ? 'var(--border)'
        : 'transparent'};
      border-radius: 50%;
    `}

    ${props =>
    props.showBorderColor &&
    (props.selectedItem === 'transparent' ||
      props.selectedItem === 'rgba(0, 0, 0, 0)') &&
    css`
      background: linear-gradient(
        to left top,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) calc(50% - 0.8px),
        rgb(255, 0, 0) 50%,
        rgba(255, 255, 255, 1) calc(50% + 0.8px),
        rgba(255, 255, 255, 1) 100%
      );
    `}
`;

const ToggleIcon = styled.button`
  border: 0;
  outline: 0;
  background-color: transparent;
`;

const SwatchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HexLabel = styled.span`
  font-size: var(--text-sm);
  color: var(--foreground);
  font-family: var(--font-primary);
`;
